import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import './DemandeDevisNew.css';

const API_URL = import.meta.env.VITE_API_URL;

// Offres de pompage solaire
const OFFRES_POMPAGE = [
  { cv: 3, kwc: 4, prix: 11000 },
  { cv: 5, kwc: 8, prix: 19000 },
  { cv: 7.5, kwc: 10, prix: 24000 },
  { cv: 10, kwc: 13, prix: 34000 },
  { cv: 15, kwc: 18, prix: 40000 },
  { cv: 20, kwc: 25, prix: 60000 },
  { cv: 25, kwc: 30, prix: 75000 }
];

const DemandeDevisComplete = ({ onNavigate }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [step, setStep] = useState(1); // 1: Choix type, 2: Formulaire
  const [selectedType, setSelectedType] = useState(null); // 'photovoltaique', 'pompage', 'both'
  const [formData, setFormData] = useState({
    // Photovoltaïque
    consommationMensuelle: '',
    periodeConsommation: 'mensuelle',
    modePaiement: 'credit',
    // Pompage
    puissanceCV: '',
    // Projet
    avisPhotovoltaique: '',
    perspective: ''
  });
  const [loading, setLoading] = useState(false);
  const [calculResult, setCalculResult] = useState(null);
  const [offrePompageSelectionnee, setOffrePompageSelectionnee] = useState(null);
  const [mesDemandes, setMesDemandes] = useState([]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    setIsAuthenticated(!!token);
    
    if (token) {
      fetchMesDemandes();
    }
  }, []);

  const fetchMesDemandes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/demandes-devis/mes-demandes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMesDemandes(response.data);
    } catch (error) {
      console.error('Erreur récupération demandes:', error);
    }
  };

  // Fonction pour calculer les résultats du photovoltaïque
  const calculateResults = (consommationMensuelle, periodeConsommation, modePaiement) => {
    if (!consommationMensuelle || consommationMensuelle <= 0) {
      setCalculResult(null);
      return;
    }

    // Calcul de la consommation annuelle selon la période
    let consommationAnnuelle;
    switch (periodeConsommation) {
      case 'mensuelle':
        consommationAnnuelle = parseFloat(consommationMensuelle) * 12;
        break;
      case 'trimestrielle':
        consommationAnnuelle = parseFloat(consommationMensuelle) * 4;
        break;
      case 'annuelle':
        consommationAnnuelle = parseFloat(consommationMensuelle);
        break;
      default:
        consommationAnnuelle = parseFloat(consommationMensuelle) * 12;
    }

    // Calcul de la puissance nécessaire
    const puissanceNecessaire = consommationAnnuelle / 1600;

    // Arrondir à la valeur commerciale la plus proche
    const valeursCommerciales = [1, 2, 3, 5, 7, 10, 15, 20, 25, 30, 40, 50];
    let puissanceCommerciale = valeursCommerciales[0];
    for (let i = 0; i < valeursCommerciales.length; i++) {
      if (Math.abs(valeursCommerciales[i] - puissanceNecessaire) < Math.abs(puissanceCommerciale - puissanceNecessaire)) {
        puissanceCommerciale = valeursCommerciales[i];
      }
    }

    // Puissance minimale de 2 kWc
    if (puissanceCommerciale < 2) {
      puissanceCommerciale = 2;
    }

    // Logique spécifique pour les puissances entre 2 et 4
    if (puissanceNecessaire > 2 && puissanceNecessaire <= 3) {
      puissanceCommerciale = 3;
    } else if (puissanceNecessaire > 3 && puissanceNecessaire <= 4) {
      puissanceCommerciale = 4;
    }

    // Vérifier si la puissance dépasse 10 kWc (installation industrielle)
    const isIndustrielle = puissanceCommerciale > 10;

    // Déterminer le type de formulaire, le prix et l'autofinancement
    let typeFormulaire;
    let prixFormulaire;
    let typeContrat;
    let autofinancement = 0;
    let coutTotal = 0;

    if (modePaiement === 'comptant') {
      typeFormulaire = 'Non applicable';
      prixFormulaire = 0;
      typeContrat = 'Contrat hors programme Prosol électrique';
    } else {
      // Prosol Elec avec crédit
      if (puissanceCommerciale <= 2) {
        typeFormulaire = 'Formulaire 7500 DT';
        prixFormulaire = 7500;
        typeContrat = 'Contrat crédit + Formulaire Prosol électrique obligatoire';
      } else if (puissanceCommerciale <= 4) {
        typeFormulaire = 'Formulaire 10000 DT';
        prixFormulaire = 10000;
        typeContrat = 'Contrat crédit + Formulaire Prosol électrique obligatoire';
      } else if (puissanceCommerciale <= 10) {
        // Installations 5-10 kWc : Prosol Elec avec autofinancement
        typeFormulaire = 'Formulaire 10000 DT';
        prixFormulaire = 10000;
        typeContrat = 'Contrat crédit + Formulaire Prosol électrique + Autofinancement';
        
        // Calcul de l'autofinancement selon la puissance
        const autofinancementMap = {
          5: 6000,
          7: 9000,
          8: 13000,
          9: 17000,
          10: 22000
        };
        
        autofinancement = autofinancementMap[puissanceCommerciale] || 0;
        coutTotal = prixFormulaire + autofinancement;
      }
    }

    setCalculResult({
      consommationAnnuelle: Math.round(consommationAnnuelle * 100) / 100,
      puissanceNecessaire: Math.round(puissanceNecessaire * 100) / 100,
      puissanceCommerciale,
      typeFormulaire,
      prixFormulaire,
      typeContrat,
      modePaiement,
      autofinancement,
      coutTotal,
      isIndustrielle
    });
  };

  const handleTypeSelection = (type) => {
    setSelectedType(type);
    setStep(2);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newFormData = {
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    };
    setFormData(newFormData);

    // Recalculer si consommation, période ou mode de paiement change (pour photovoltaïque)
    if ((selectedType === 'photovoltaique' || selectedType === 'both') && 
        (name === 'consommationMensuelle' || name === 'periodeConsommation' || name === 'modePaiement')) {
      calculateResults(
        name === 'consommationMensuelle' ? value : newFormData.consommationMensuelle,
        name === 'periodeConsommation' ? value : newFormData.periodeConsommation,
        name === 'modePaiement' ? value : newFormData.modePaiement
      );
    }

    // Mettre à jour l'offre de pompage si la puissance CV change
    if (name === 'puissanceCV' && value) {
      const cvValue = parseFloat(value);
      // Chercher d'abord une correspondance exacte
      let offre = OFFRES_POMPAGE.find(o => o.cv === cvValue);
      
      // Si pas de correspondance exacte, toujours prendre la puissance SUPÉRIEURE
      if (!offre && cvValue > 0) {
        // Filtrer les offres supérieures à la valeur saisie
        const offresSupérieures = OFFRES_POMPAGE.filter(o => o.cv > cvValue);
        
        if (offresSupérieures.length > 0) {
          // Prendre la plus petite des offres supérieures
          offre = offresSupérieures[0]; // Les offres sont déjà triées par CV croissant
        } else {
          // Si aucune offre supérieure, prendre la plus grande offre disponible
          offre = OFFRES_POMPAGE[OFFRES_POMPAGE.length - 1];
        }
      }
      
      setOffrePompageSelectionnee(offre || null);
    }
  };

  // Fonction pour sélectionner une offre par clic
  const handleOffrePompageClick = (offre) => {
    setFormData({
      ...formData,
      puissanceCV: offre.cv
    });
    setOffrePompageSelectionnee(offre);
  };

  const getStatutBadgeClass = (statut) => {
    switch (statut) {
      case 'validé':
        return 'badge-success';
      case 'rejeté':
        return 'badge-danger';
      case 'en_cours':
        return 'badge-info';
      default:
        return 'badge-warning';
    }
  };

  const getStatutLabel = (statut) => {
    switch (statut) {
      case 'en_cours':
        return 'En cours';
      case 'en_attente':
        return 'En attente';
      case 'validé':
        return 'Validé';
      case 'rejeté':
        return 'Rejeté';
      default:
        return statut;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isAuthenticated) {
      Swal.fire({
        icon: 'warning',
        title: 'Connexion requise',
        text: 'Vous devez être connecté pour faire une demande de devis.',
        confirmButtonText: 'Se connecter'
      }).then((result) => {
        if (result.isConfirmed && onNavigate) {
          onNavigate('login');
        }
      });
      return;
    }

    // Validation
    if ((selectedType === 'photovoltaique' || selectedType === 'both') && 
        (!formData.consommationMensuelle || formData.consommationMensuelle < 0)) {
      Swal.fire({
        icon: 'error',
        title: 'Erreur',
        text: 'Veuillez entrer une consommation mensuelle valide.'
      });
      return;
    }

    if ((selectedType === 'pompage' || selectedType === 'both') && !formData.puissanceCV) {
      Swal.fire({
        icon: 'error',
        title: 'Erreur',
        text: 'Veuillez indiquer la puissance de votre pompe.'
      });
      return;
    }

    if ((selectedType === 'pompage' || selectedType === 'both') && formData.puissanceCV) {
      const cvValue = parseFloat(formData.puissanceCV);
      if (cvValue <= 0 || cvValue > 50) {
        Swal.fire({
          icon: 'error',
          title: 'Erreur',
          text: 'La puissance de la pompe doit être entre 1 et 50 CV.'
        });
        return;
      }
    }

    // Validation pour photovoltaïque uniquement
    if (selectedType === 'photovoltaique' || selectedType === 'both') {
      if (!formData.avisPhotovoltaique.trim()) {
        Swal.fire({
          icon: 'error',
          title: 'Erreur',
          text: 'Veuillez partager votre avis sur le photovoltaïque.'
        });
        return;
      }

      if (!formData.perspective.trim()) {
        Swal.fire({
          icon: 'error',
          title: 'Erreur',
          text: 'Veuillez décrire votre perspective.'
        });
        return;
      }
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      
      const requestData = {};

      // Données photovoltaïque
      if (selectedType === 'photovoltaique' || selectedType === 'both') {
        requestData.consommationMensuelle = parseFloat(formData.consommationMensuelle);
        requestData.periodeConsommation = formData.periodeConsommation;
        requestData.modePaiement = formData.modePaiement;
        requestData.pompage = false;
        requestData.avisPhotovoltaique = formData.avisPhotovoltaique;
        requestData.perspective = formData.perspective;
      }
      
      // Données pompage
      if (selectedType === 'pompage' || selectedType === 'both') {
        requestData.pompage = true;
        requestData.typePompage = 'autonome_sans_steg';
        requestData.puissanceCV = parseFloat(formData.puissanceCV);
        
        // Pour le pompage seul, mode de paiement par défaut = comptant (pas de crédit Prosol)
        if (selectedType === 'pompage') {
          requestData.modePaiement = 'comptant';
        }
        
        if (offrePompageSelectionnee) {
          requestData.puissanceKWC = offrePompageSelectionnee.kwc;
          requestData.prixPompage = offrePompageSelectionnee.prix;
        }
      }

      await axios.post(
        `${API_URL}/demandes-devis`,
        requestData,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      Swal.fire({
        icon: 'success',
        title: 'Demande envoyée',
        text: 'Votre demande de devis a été envoyée avec succès. Nous vous contacterons bientôt.',
        confirmButtonText: 'OK'
      });

      // Réinitialiser
      setFormData({
        consommationMensuelle: '',
        periodeConsommation: 'mensuelle',
        modePaiement: 'credit',
        puissanceCV: '',
        avisPhotovoltaique: '',
        perspective: ''
      });
      setCalculResult(null);
      setOffrePompageSelectionnee(null);
      setStep(1);
      setSelectedType(null);
      fetchMesDemandes();
    } catch (error) {
      console.error('Erreur envoi demande:', error);
      Swal.fire({
        icon: 'error',
        title: 'Erreur',
        text: error.response?.data?.message || 'Erreur lors de l\'envoi de la demande.'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="devis-new-page">
      {/* Hero Section */}
      <section className="devis-new-hero">
        <div className="container">
          <div className="devis-hero-content">
            <br />
            <h1 className="devis-title">Demander un Devis</h1>
            <p className="devis-subtitle">
              Obtenez une estimation personnalisée pour votre installation solaire
            </p>
          </div>
        </div>
      </section>

      {!isAuthenticated ? (
        <div className="container">
          <div className="auth-required-box">
            <div className="auth-icon">🔒</div>
            <h2>Connexion requise</h2>
            <p>Vous devez être connecté pour faire une demande de devis.</p>
            <button className="btn-login" onClick={() => onNavigate && onNavigate('login')}>
              Se connecter / S'inscrire
            </button>
          </div>
        </div>
      ) : (
        <div className="container">
          {/* Section Avantages */}
          {step === 1 && (
            <>
              <section className="advantages-section">
                <h2 className="section-title">Pourquoi choisir l'énergie solaire ?</h2>
                <div className="advantages-grid">
                  <div className="advantage-card">
                    <div className="advantage-icon savings">💰</div>
                    <h3>Économies substantielles</h3>
                    <p>Réduisez vos factures d'électricité jusqu'à 70%</p>
                  </div>
                  <div className="advantage-card">
                    <div className="advantage-icon eco">🌍</div>
                    <h3>Écologique</h3>
                    <p>Participez à la protection de l'environnement</p>
                  </div>
                  <div className="advantage-card">
                    <div className="advantage-icon autonomy">⚡</div>
                    <h3>Autonomie énergétique</h3>
                    <p>Produisez votre propre électricité</p>
                  </div>
                  <div className="advantage-card">
                    <div className="advantage-icon investment">📈</div>
                    <h3>Investissement rentable</h3>
                    <p>Retour sur investissement en 5 à 7 ans</p>
                  </div>
                </div>
              </section>

              {/* Section Choix du Type de Devis */}
              <section className="type-selection-section">
                <h2 className="section-title gradient-text-animated">Choisissez votre type d'installation</h2>
                <p className="section-subtitle gradient-subtitle">Sélectionnez le type de devis qui correspond à vos besoins</p>
                
                <div className="type-cards-grid">
                  {/* Option 1 - Photovoltaïque */}
                  <div className="type-card" onClick={() => handleTypeSelection('photovoltaique')}>
                    <div className="type-card-icon photovoltaique">
                      <svg viewBox="0 0 100 100" width="80" height="80">
                        <rect x="10" y="20" width="80" height="60" fill="#1e3a5f" rx="5"/>
                        <g fill="#FFD700" opacity="0.9">
                          <rect x="15" y="25" width="18" height="18"/>
                          <rect x="36" y="25" width="18" height="18"/>
                          <rect x="57" y="25" width="18" height="18"/>
                          <rect x="15" y="46" width="18" height="18"/>
                          <rect x="36" y="46" width="18" height="18"/>
                          <rect x="57" y="46" width="18" height="18"/>
                        </g>
                        <circle cx="50" cy="15" r="8" fill="#FFD700"/>
                        <path d="M50 7 L50 23 M42 15 L58 15 M44 9 L56 9 M44 21 L56 21" stroke="#FFD700" strokeWidth="2"/>
                      </svg>
                    </div>
                    <h3>Installation Photovoltaïque</h3>
                    <p>Pour maison, entreprise ou industrie</p>
                    <ul className="type-features">
                      <li>✓ Panneaux solaires</li>
                      <li>✓ Onduleur</li>
                      <li>✓ Installation complète</li>
                    </ul>
                    <button className="btn-select-type">Choisir ce devis</button>
                  </div>

                  {/* Option 2 - Pompage Solaire */}
                  <div className="type-card" onClick={() => handleTypeSelection('pompage')}>
                    <div className="type-card-icon pompage">
                      <svg viewBox="0 0 100 100" width="80" height="80">
                        <rect x="30" y="50" width="40" height="30" fill="#1e3a5f" rx="3"/>
                        <circle cx="50" cy="65" r="8" fill="#3b82f6"/>
                        <path d="M45 30 Q50 35, 55 30 M45 40 Q50 45, 55 40" stroke="#3b82f6" strokeWidth="3" fill="none"/>
                        <rect x="20" y="10" width="60" height="30" fill="#FFD700" rx="5" opacity="0.8"/>
                        <g fill="#1e3a5f">
                          <rect x="25" y="15" width="12" height="12"/>
                          <rect x="40" y="15" width="12" height="12"/>
                          <rect x="55" y="15" width="12" height="12"/>
                        </g>
                        <path d="M50 80 L50 90 M45 85 L55 85" stroke="#3b82f6" strokeWidth="3"/>
                      </svg>
                    </div>
                    <h3>Pompage Solaire</h3>
                    <p>Pour usage agricole et irrigation</p>
                    <ul className="type-features">
                      <li>✓ Pompe solaire</li>
                      <li>✓ Système autonome</li>
                      <li>✓ 0 DT facture STEG</li>
                    </ul>
                    <button className="btn-select-type">Choisir ce devis</button>
                  </div>

                  {/* Option 3 - Les deux */}
                  <div className="type-card featured" onClick={() => handleTypeSelection('both')}>
                    <div className="featured-badge">Recommandé</div>
                    <div className="type-card-icon both">
                      <svg viewBox="0 0 100 100" width="80" height="80">
                        <rect x="5" y="15" width="40" height="30" fill="#1e3a5f" rx="3"/>
                        <g fill="#FFD700" opacity="0.9">
                          <rect x="10" y="20" width="9" height="9"/>
                          <rect x="22" y="20" width="9" height="9"/>
                          <rect x="34" y="20" width="9" height="9"/>
                          <rect x="10" y="32" width="9" height="9"/>
                          <rect x="22" y="32" width="9" height="9"/>
                          <rect x="34" y="32" width="9" height="9"/>
                        </g>
                        <rect x="55" y="50" width="40" height="30" fill="#1e3a5f" rx="3"/>
                        <circle cx="75" cy="65" r="8" fill="#3b82f6"/>
                        <path d="M70 30 Q75 35, 80 30 M70 40 Q75 45, 80 40" stroke="#3b82f6" strokeWidth="3" fill="none"/>
                        <circle cx="50" cy="50" r="20" fill="#FFD700"/>
                        <text x="50" y="57" fontSize="24" fill="#1e3a5f" textAnchor="middle" fontWeight="bold">+</text>
                      </svg>
                    </div>
                    <h3>Les deux installations</h3>
                    <p>Solution complète pour un maximum d'économies</p>
                    <ul className="type-features">
                      <li>✓ Photovoltaïque + Pompage</li>
                      <li>✓ Économies maximales</li>
                      <li>✓ Autonomie totale</li>
                    </ul>
                    <button className="btn-select-type primary">Demander les deux</button>
                  </div>
                </div>
              </section>

              {/* Mes demandes précédentes */}
              {mesDemandes.length > 0 && (
                <section className="mes-demandes-section">
                  <h3>Mes demandes précédentes</h3>
                  <div className="demandes-list">
                    {mesDemandes.map((demande) => (
                      <div key={demande._id} className="demande-card">
                        <div className="demande-header">
                          <span className="demande-date">
                            {new Date(demande.createdAt).toLocaleDateString('fr-FR')}
                          </span>
                          <span className={`badge ${getStatutBadgeClass(demande.statut)}`}>
                            {getStatutLabel(demande.statut)}
                          </span>
                        </div>
                        <div className="demande-details">
                          <p><strong>Consommation {demande.periodeConsommation || 'mensuelle'}:</strong> {demande.consommationMensuelle} kWh</p>
                          {demande.consommationAnnuelle && (
                            <p><strong>Consommation annuelle:</strong> {demande.consommationAnnuelle} kWh/an</p>
                          )}
                          {demande.pompage && (
                            <p><strong>Consommation pompage:</strong> {demande.consommationPompage} kWh</p>
                          )}
                          {demande.puissanceCommerciale && (
                            <p><strong>Puissance recommandée:</strong> {demande.puissanceCommerciale} kWc</p>
                          )}
                          {demande.modePaiement && (
                            <p><strong>Mode de paiement:</strong> {demande.modePaiement === 'comptant' ? 'Comptant' : 'Crédit'}</p>
                          )}
                          {demande.typeFormulaireProsol && demande.modePaiement === 'credit' && (
                            <p><strong>Formulaire Prosol:</strong> {demande.typeFormulaireProsol}</p>
                          )}
                          {demande.prixFormulaire > 0 && (
                            <p><strong>Prix formulaire:</strong> <span style={{color: '#4caf50', fontWeight: 'bold'}}>{demande.prixFormulaire} DT</span></p>
                          )}
                          {demande.commentaireAdmin && (
                            <div className="commentaire-admin">
                              <strong>Commentaire:</strong>
                              <p>{demande.commentaireAdmin}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              )}
            </>
          )}

          {/* Étape 2 - Formulaire selon le type */}
          {step === 2 && (
            <section className="form-section">
              <button className="btn-back" onClick={() => { setStep(1); setCalculResult(null); }}>
                ← Retour
              </button>
              
              <div className="form-header">
                <h2>Formulaire de demande de devis</h2>
                <p className="form-type-badge">
                  {selectedType === 'photovoltaique' && '⚡ Installation Photovoltaïque'}
                  {selectedType === 'pompage' && '💧 Pompage Solaire'}
                  {selectedType === 'both' && '🌟 Photovoltaïque + Pompage'}
                </p>
              </div>

              <form onSubmit={handleSubmit} className="devis-form-new">
                {/* === PHOTOVOLTAÏQUE === */}
                {(selectedType === 'photovoltaique' || selectedType === 'both') && (
                  <div className="form-block">
                    <h3 className="form-block-title">
                      <span className="block-icon">⚡</span>
                      Installation Photovoltaïque
                    </h3>
                    
                    <div className="form-row">
                      <div className="form-field">
                        <label htmlFor="periodeConsommation">Période de consommation *</label>
                        <select
                          id="periodeConsommation"
                          name="periodeConsommation"
                          value={formData.periodeConsommation}
                          onChange={handleChange}
                          required
                        >
                          <option value="mensuelle">Mensuelle (par mois)</option>
                          <option value="trimestrielle">Trimestrielle (par 3 mois)</option>
                          <option value="annuelle">Annuelle (par an)</option>
                        </select>
                        <small>Choisissez la période correspondant à votre facture</small>
                      </div>

                      <div className="form-field">
                        <label htmlFor="consommationMensuelle">
                          Consommation {formData.periodeConsommation === 'mensuelle' ? 'mensuelle' : formData.periodeConsommation === 'trimestrielle' ? 'trimestrielle' : 'annuelle'} (kWh) *
                        </label>
                        <input
                          type="number"
                          id="consommationMensuelle"
                          name="consommationMensuelle"
                          value={formData.consommationMensuelle}
                          onChange={handleChange}
                          required
                          min="0"
                          step="0.01"
                          placeholder={formData.periodeConsommation === 'mensuelle' ? 'Ex: 350' : formData.periodeConsommation === 'trimestrielle' ? 'Ex: 1050' : 'Ex: 4200'}
                        />
                        <small>Vous pouvez trouver cette information sur votre facture d'électricité</small>
                      </div>
                    </div>

                    <div className="form-field">
                      <label htmlFor="modePaiement">Mode de paiement *</label>
                      <select
                        id="modePaiement"
                        name="modePaiement"
                        value={formData.modePaiement}
                        onChange={handleChange}
                        required
                        className="form-select"
                      >
                        <option value="credit">Paiement par crédit</option>
                        <option value="comptant">Paiement comptant</option>
                      </select>
                      <small>
                        {formData.modePaiement === 'credit' 
                          ? 'Le crédit nécessite un formulaire Prosol électrique' 
                          : 'Le paiement comptant est hors programme Prosol électrique'}
                      </small>
                    </div>

                    {/* Résultats du calcul */}
                    {calculResult && (
                      <div className="calcul-result">
                        <h4>📊 Estimation de votre installation</h4>
                        <div className="result-grid">
                          <div className="result-item">
                            <span className="result-label">Consommation annuelle:</span>
                            <span className="result-value">{calculResult.consommationAnnuelle} kWh/an</span>
                          </div>
                          <div className="result-item">
                            <span className="result-label">Puissance calculée:</span>
                            <span className="result-value">{calculResult.puissanceNecessaire} kWc</span>
                          </div>
                          {!calculResult.isIndustrielle && (
                            <>
                              <div className="result-item highlight">
                                <span className="result-label">Puissance recommandée:</span>
                                <span className="result-value">{calculResult.puissanceCommerciale} kWc</span>
                              </div>
                              <div className="result-item">
                                <span className="result-label">Type de contrat:</span>
                                <span className="result-value">{calculResult.typeContrat}</span>
                              </div>
                              {calculResult.modePaiement === 'credit' && (
                                <>
                                  <div className="result-item highlight">
                                    <span className="result-label">Formulaire Prosol requis:</span>
                                    <span className="result-value">{calculResult.typeFormulaire}</span>
                                  </div>
                                  <div className="result-item price-item">
                                    <span className="result-label">💰 Prix du formulaire:</span>
                                    <span className="result-value price">{calculResult.prixFormulaire} DT</span>
                                  </div>
                                  {calculResult.autofinancement > 0 && (
                                    <>
                                      <div className="result-item" style={{
                                        background: 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)',
                                        border: '2px solid #ff9800'
                                      }}>
                                        <span className="result-label">🏦 Autofinancement requis:</span>
                                        <span className="result-value" style={{color: '#f57c00'}}>{calculResult.autofinancement} DT</span>
                                      </div>
                                      <div className="result-item" style={{
                                        background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
                                        border: '3px solid #2196f3'
                                      }}>
                                        <span className="result-label">💵 Coût total:</span>
                                        <span className="result-value" style={{color: '#1976d2', fontSize: '1.6rem'}}>{calculResult.coutTotal} DT</span>
                                      </div>
                                    </>
                                  )}
                                </>
                              )}
                            </>
                          )}
                        </div>
                        {!calculResult.isIndustrielle && (
                          <div className="prosol-info">
                            <h5>📋 Documents requis:</h5>
                            <ul>
                              <li>✓ Contrat à signer</li>
                              <li>✓ Signature obligatoire à la <strong>municipalité</strong></li>
                              <li>✓ Dépôt au siège de <strong>SASTRA</strong></li>
                              {calculResult.modePaiement === 'credit' && (
                                <li>✓ Formulaire Prosol électrique <strong>{calculResult.typeFormulaire}</strong></li>
                              )}
                            </ul>
                            
                            {calculResult.autofinancement > 0 && (
                              <div style={{
                                marginTop: '15px',
                                padding: '15px',
                                background: 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)',
                                borderRadius: '10px',
                                borderLeft: '5px solid #ff9800'
                              }}>
                                <h5 style={{
                                  color: '#e65100',
                                  fontSize: '1.1rem',
                                  margin: '0 0 10px 0',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px'
                                }}>
                                  🏦 Autofinancement Prosol Elec
                                </h5>
                                <p style={{
                                  color: '#bf360c',
                                  fontSize: '0.95rem',
                                  lineHeight: '1.6',
                                  margin: '0 0 10px 0'
                                }}>
                                  Pour les installations de <strong>{calculResult.puissanceCommerciale} kWc</strong>, le programme Prosol Elec 
                                  nécessite un <strong>autofinancement de {calculResult.autofinancement} DT</strong> en plus du formulaire d'adhésion.
                                </p>
                                <p style={{
                                  color: '#ef6c00',
                                  fontSize: '0.9rem',
                                  margin: '0',
                                  fontWeight: '600'
                                }}>
                                  💡 Montant total à prévoir : <strong style={{fontSize: '1.1rem'}}>{calculResult.coutTotal} DT</strong>
                                  <br />
                                  <span style={{fontSize: '0.85rem', fontWeight: '400'}}>
                                    ({calculResult.prixFormulaire} DT formulaire + {calculResult.autofinancement} DT autofinancement)
                                  </span>
                                </p>
                              </div>
                            )}
                          </div>
                        )}
                        
                        {calculResult.isIndustrielle && (
                          <div className="alert-industrielle" style={{
                            marginTop: '20px',
                            padding: '20px',
                            background: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)',
                            borderRadius: '12px',
                            border: '3px solid #ef4444',
                            boxShadow: '0 4px 12px rgba(239, 68, 68, 0.2)'
                          }}>
                            <h4 style={{
                              margin: '0 0 15px 0',
                              color: '#991b1b',
                              fontSize: '1.1rem',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '10px'
                            }}>
                              <span style={{fontSize: '1.5rem'}}>⚠️</span>
                              Installation Industrielle
                            </h4>
                            <p style={{
                              color: '#7f1d1d',
                              fontSize: '0.95rem',
                              lineHeight: '1.6',
                              margin: '0 0 15px 0',
                              fontWeight: '600'
                            }}>
                              Votre consommation nécessite une installation de plus de 10 kWc. Ce type d'installation est destiné aux entreprises et nécessite une étude personnalisée.
                            </p>
                            <div style={{
                              background: 'white',
                              padding: '15px',
                              borderRadius: '8px',
                              marginBottom: '15px'
                            }}>
                              <h5 style={{margin: '0 0 12px 0', color: '#991b1b', fontSize: '0.95rem'}}>
                                📞 Veuillez contacter notre service client :
                              </h5>
                              <div style={{fontSize: '0.9rem', color: '#1e3a5f', lineHeight: '1.8'}}>
                                <p style={{margin: '5px 0', display: 'flex', alignItems: 'center', gap: '8px'}}>
                                  <strong>📍 Adresse :</strong> Résidence MAAROUFI, AV Habib Bourguiba, Bardo 2000
                                </p>
                                <p style={{margin: '5px 0', display: 'flex', alignItems: 'center', gap: '8px'}}>
                                  <strong>📞 Téléphone :</strong> +216 36 437 473 / +216 93 454 400
                                </p>
                                <p style={{margin: '5px 0', display: 'flex', alignItems: 'center', gap: '8px'}}>
                                  <strong>📧 Email :</strong> info@sastraenergy.com
                                </p>
                              </div>
                            </div>
                            <div style={{
                              background: '#fef3c7',
                              padding: '12px',
                              borderRadius: '6px',
                              borderLeft: '4px solid #f59e0b'
                            }}>
                              <p style={{
                                margin: '0 0 10px 0',
                                fontSize: '0.9rem',
                                color: '#92400e',
                                fontWeight: '600'
                              }}>
                                💡 Vous pouvez également trouver le bureau le plus proche de vous :
                              </p>
                              <button
                                type="button"
                                onClick={() => onNavigate && onNavigate('bureaux')}
                                style={{
                                  background: 'linear-gradient(135deg, #1e3a5f 0%, #3b82f6 100%)',
                                  color: 'white',
                                  border: 'none',
                                  padding: '10px 20px',
                                  borderRadius: '8px',
                                  cursor: 'pointer',
                                  fontWeight: '700',
                                  fontSize: '0.9rem',
                                  width: '100%',
                                  boxShadow: '0 2px 8px rgba(30, 58, 95, 0.3)',
                                  transition: 'all 0.3s ease'
                                }}
                              >
                                📍 Consulter Nos Bureaux
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* === POMPAGE SOLAIRE === */}
                {(selectedType === 'pompage' || selectedType === 'both') && !calculResult?.isIndustrielle && (
                  <div className="form-block">
                    <h3 className="form-block-title">
                      <span className="block-icon">⚡</span>
                      Pompage Solaire Autonome
                    </h3>
                    <p style={{color: '#64748b', fontSize: '0.95rem', marginTop: '-10px', marginBottom: '20px'}}>
                      Solution 100% autonome - Zéro facture STEG
                    </p>
                    
                    <div className="pompage-autonome-info">
                      <h4>Système de Pompage Solaire Autonome</h4>
                      <p>Solution énergétique 100% autonome et durable pour vos besoins de pompage. Indiquez la puissance de votre pompe en chevaux (CV) pour obtenir une recommandation personnalisée.</p>
                    </div>

                    <div className="form-field">
                      <label htmlFor="puissanceCV">Puissance de la pompe (CV) *</label>
                      <input
                        type="number"
                        id="puissanceCV"
                        name="puissanceCV"
                        value={formData.puissanceCV}
                        onChange={handleChange}
                        required
                        min="1"
                        max="50"
                        step="0.5"
                        placeholder="Ex: 5 ou 7.5"
                        className="form-input"
                      />
                      <small>Cliquez sur une offre ci-dessous ou saisissez manuellement la puissance en CV</small>
                    </div>

                    {/* Afficher les offres disponibles */}
                    <div className="pompage-offers-container">
                      <div className="pompage-offers-header">
                        <h4>Nos Offres de Pompage Solaire</h4>
                        <p>Sélectionnez l'offre qui correspond le mieux à vos besoins</p>
                      </div>
                      
                      <div className="pompage-offers-grid">
                        {OFFRES_POMPAGE.map(offre => {
                          const isSelected = offrePompageSelectionnee && offrePompageSelectionnee.cv === offre.cv;
                          const isExactMatch = formData.puissanceCV && parseFloat(formData.puissanceCV) === offre.cv;
                          const cardClass = isExactMatch ? 'selected' : (isSelected && !isExactMatch ? 'recommended' : '');
                          
                          return (
                            <div 
                              key={offre.cv} 
                              className={`pompage-offer-card ${cardClass}`}
                              onClick={() => handleOffrePompageClick(offre)}
                              style={{cursor: 'pointer'}}
                            >
                              {isExactMatch && (
                                <div className="pompage-offer-badge selected">
                                  Sélectionné
                                </div>
                              )}
                              {isSelected && !isExactMatch && (
                                <div className="pompage-offer-badge recommended">
                                  Recommandé
                                </div>
                              )}
                              
                              <div className="pompage-offer-header-card">
                                <div className="pompage-offer-cv">{offre.cv}</div>
                                <div className="pompage-offer-cv-label">Chevaux (CV)</div>
                              </div>
                              
                              <div className="pompage-offer-body">
                                <div className="pompage-offer-specs">
                                  <div className="pompage-spec-item">
                                    <span className="pompage-spec-label">Puissance Solaire</span>
                                    <span className="pompage-spec-value">{offre.kwc} kWc</span>
                                  </div>
                                </div>
                                
                                <div className="pompage-offer-price-section">
                                  <div className="pompage-price-label">Prix Total</div>
                                  <div className="pompage-price-amount">
                                    {offre.prix.toLocaleString()}
                                    <span className="pompage-price-currency">DT</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {offrePompageSelectionnee && formData.puissanceCV && (
                      <div>
                        {parseFloat(formData.puissanceCV) !== offrePompageSelectionnee.cv && (
                          <div className="pompage-alert-info">
                            <div className="pompage-alert-icon">i</div>
                            <div className="pompage-alert-content">
                              <p>
                                Aucune offre exacte pour <strong>{formData.puissanceCV} CV</strong>. 
                                Nous vous recommandons l'offre supérieure : <strong>{offrePompageSelectionnee.cv} CV</strong> 
                                {offrePompageSelectionnee.cv > parseFloat(formData.puissanceCV) ? 
                                  ' (puissance adaptée à vos besoins)' : 
                                  ' (offre maximale disponible)'
                                }.
                              </p>
                            </div>
                          </div>
                        )}
                        
                        <div className="pompage-details-section">
                          <div className="pompage-details-header">
                            <h4>Détails de Votre Système de Pompage</h4>
                          </div>
                          
                          <div className="pompage-specs-grid">
                            <div className="pompage-spec-card">
                              <div className="pompage-spec-card-label">Puissance Moteur</div>
                              <div className="pompage-spec-card-value">{offrePompageSelectionnee.cv} CV</div>
                            </div>
                            
                            <div className="pompage-spec-card">
                              <div className="pompage-spec-card-label">Puissance Solaire</div>
                              <div className="pompage-spec-card-value">{offrePompageSelectionnee.kwc} kWc</div>
                            </div>
                            
                            <div className="pompage-price-card">
                              <div className="pompage-price-card-label">Prix Total du Système</div>
                              <div className="pompage-price-card-value">
                                {offrePompageSelectionnee.prix.toLocaleString()} DT
                              </div>
                            </div>
                          </div>
                          
                          <div className="pompage-advantages-section">
                            <h5 className="pompage-advantages-header">Avantages du Pompage Solaire Autonome</h5>
                            <ul className="pompage-advantages-list">
                              <li className="pompage-advantage-item">
                                <div className="pompage-advantage-icon">✓</div>
                                <div className="pompage-advantage-text">
                                  <strong>0 DT de facture STEG</strong> - Système 100% autonome
                                </div>
                              </li>
                              <li className="pompage-advantage-item">
                                <div className="pompage-advantage-icon">✓</div>
                                <div className="pompage-advantage-text">
                                  Installation complète clé en main
                                </div>
                              </li>
                              <li className="pompage-advantage-item">
                                <div className="pompage-advantage-icon">✓</div>
                                <div className="pompage-advantage-text">
                                  Garantie constructeur
                                </div>
                              </li>
                              <li className="pompage-advantage-item">
                                <div className="pompage-advantage-icon">✓</div>
                                <div className="pompage-advantage-text">
                                  Maintenance et suivi professionnel
                                </div>
                              </li>
                              <li className="pompage-advantage-item">
                                <div className="pompage-advantage-icon">✓</div>
                                <div className="pompage-advantage-text">
                                  Rentabilité assurée dès la première année
                                </div>
                              </li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* === DÉTAILS DU PROJET === */}
                {/* Cette section n'est requise que pour photovoltaïque */}
                {(selectedType === 'photovoltaique' || selectedType === 'both') && !calculResult?.isIndustrielle && (
                  <div className="form-block">
                    <h3 className="form-block-title">
                      <span className="block-icon">✍</span>
                      Détails de votre projet
                    </h3>

                    <div className="form-field">
                      <label htmlFor="avisPhotovoltaique">
                        Que pensez-vous du photovoltaïque ? *
                      </label>
                      <textarea
                        id="avisPhotovoltaique"
                        name="avisPhotovoltaique"
                        value={formData.avisPhotovoltaique}
                        onChange={handleChange}
                        required
                        maxLength="1000"
                        rows="4"
                        placeholder="Partagez votre opinion sur l'énergie solaire..."
                      />
                      <small className="char-count">{formData.avisPhotovoltaique.length}/1000 caractères</small>
                    </div>

                    <div className="form-field">
                      <label htmlFor="perspective">
                        Quelle est votre perspective/objectif ? *
                      </label>
                      <textarea
                        id="perspective"
                        name="perspective"
                        value={formData.perspective}
                        onChange={handleChange}
                        required
                        maxLength="1000"
                        rows="4"
                        placeholder="Décrivez vos objectifs (économies, autonomie, écologie...)..."
                      />
                      <small className="char-count">{formData.perspective.length}/1000 caractères</small>
                    </div>
                  </div>
                )}

                {/* Boutons de soumission */}
                {!calculResult?.isIndustrielle && (
                  <div className="form-actions">
                    <button type="button" className="btn-secondary" onClick={() => { setStep(1); setCalculResult(null); }}>
                      Annuler
                    </button>
                    <button type="submit" className="btn-primary" disabled={loading}>
                      {loading ? 'Envoi en cours...' : 'Envoyer la demande'}
                    </button>
                  </div>
                )}
              </form>
            </section>
          )}
        </div>
      )}
    </div>
  );
};

export default DemandeDevisComplete;
